import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { type Options as MarkdownOptions } from 'react-markdown'
import {
  useActionData,
  useLoaderData,
  type ActionFunctionArgs,
  data,
  type LoaderFunctionArgs,
  type MetaFunction,
} from 'react-router'
import { Navigation, Pagination, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  ContactForm,
  INTENT_SEND_CONTACT_REQUEST,
  sendContactRequest,
  CONTACT_FORM_CONFIRMATION_CONTENT_TYPE,
} from '#app/components/contact-form.tsx'
import { DecoratedImage } from '#app/components/decorated-image.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Markdown } from '#app/components/markdown.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { type ApplicationContent } from '#app/types/application-content.ts'
import { type ImageAsset } from '#app/types/multimedia.ts'
import { getApplicationContent } from '#app/utils/application-content.server.js'
import { getApplicationImages } from '#app/utils/application-image.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { groupedByContentType } from '#app/utils/grouped-content.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
  getLocalizedContent,
  getLocalizedImageUrl,
  type LocalizedContent,
} from '#app/utils/i18n.ts'
import { Status } from '#app/utils/status.ts'

enum OurStoryContent {
  ABSTRACT = 'Our Story Abstract',
  LEADERSHIP_TEXT = 'Our Story Leadership Text',
  LEADERSHIP_TITLE = 'Our Story Leadership Title',
  TEXT = 'Our Story Text',
  TITLE = 'Our Story Title',
  CONTACT_FORM_HEADING = 'Our Story Contact Form Heading',
  CONTACT_FORM_DESCRIPTION = 'Our Story Contact Form Description',
  CONTACT_FORM_BUTTON = 'Our Story Contact Form Button',
}

export async function loader({ context }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const errorData = {
    title: t('errors.general.title'),
    description: t('errors.general.description'),
  }

  const pageTitle = t('ourStory', locale)

  const contentTypePrefix = 'Our Story'

  const ourStoryContentQuery = `*[_type == 'applicationContent' && contentType match '${contentTypePrefix}*']`
  const ourStoryContentResult = await executeQuery(ourStoryContentQuery)

  if (ourStoryContentResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const ourStoryContentData = ourStoryContentResult.data as ApplicationContent[]

  const getImageAssetWithText = (imgAsset: ImageAsset | undefined) => {
    const url = imgAsset
      ? getLocalizedImageUrl(imgAsset.localizedContent, locale)
      : undefined
    return {
      caption: imgAsset?.caption,
      alt: imgAsset?.alt,
      url,
    }
  }

  const ourStoryPhoto1Result = await getApplicationImages(
    `${contentTypePrefix} Photo 1`,
  )
  const ourStoryPhoto2Result = await getApplicationImages(
    `${contentTypePrefix} Photo 2`,
  )

  if (
    ourStoryPhoto1Result.status !== Status.Success ||
    ourStoryPhoto2Result.status !== Status.Success
  ) {
    throw data(errorData, { status: 500 })
  }

  const ourStoryPhotosResult = [ourStoryPhoto1Result, ourStoryPhoto2Result]

  const ourStoryCarouselResult = await getApplicationImages(
    `${contentTypePrefix} Carousel`,
  )

  if (ourStoryCarouselResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const ourStoryPhotos = ourStoryPhotosResult.map(
    (result: any) => result.images as ImageAsset[],
  )
  const ourStoryCarousel = ourStoryCarouselResult.images?.map(
    (result: any) => result as ImageAsset,
  )

  const ourStoryPhotosURLs = ourStoryPhotos.map(imgAsset =>
    getImageAssetWithText(imgAsset[0]),
  )
  const ourStoryCarouselItems = ourStoryCarousel?.map(carouselImgAsset =>
    getImageAssetWithText(carouselImgAsset),
  )

  const contactFormConfirmationResult = await getApplicationContent(
    CONTACT_FORM_CONFIRMATION_CONTENT_TYPE,
  )

  if (
    contactFormConfirmationResult.status !== Status.Success ||
    !contactFormConfirmationResult.content
  ) {
    throw data(errorData, { status: 500 })
  }

  const contactFormConfirmation = contactFormConfirmationResult.content

  return {
    pageTitle,
    textContent: groupedByContentType(ourStoryContentData),
    ourStoryPhotosResult,
    ourStoryPhotos: ourStoryPhotosURLs,
    ourStoryCarouselItems,
    contactFormConfirmation:
      contactFormConfirmation.content as LocalizedContent,
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  await checkHoneypot(formData)
  const intent = formData.get('intent')

  switch (intent) {
    case INTENT_SEND_CONTACT_REQUEST: {
      const request = await sendContactRequest(formData)

      if (request.status !== Status.Success) {
        return { status: Status.Error }
      }

      return { success: true, contactFormSubmitted: true }
    }
    default: {
      throw new Response(`${intent} intent is not supported`, {
        status: 400,
      })
    }
  }
}

export default function OurStoryRoute() {
  const {
    textContent,
    ourStoryPhotos,
    ourStoryCarouselItems,
    contactFormConfirmation,
  } = useLoaderData<typeof loader>()

  const actionData = useActionData<typeof action>()
  const { t, i18n } = useTranslation()
  const locale = i18n.language

  const MarkdownParagraph = ({ children }: { children: ReactNode }) => {
    return <p className="font-poppins text-2 lg:text-3 mb-5">{children}</p>
  }
  const markdownOptions: MarkdownOptions = {
    components: {
      p(props) {
        const { children } = props
        return <MarkdownParagraph>{children}</MarkdownParagraph>
      },
    },
  }

  const desktopPhoto1 = {
    image: {
      url: ourStoryPhotos[0]?.url,
      width: 536,
      height: 434,
      marginTop: 134,
      caption: ourStoryPhotos[0]?.caption as LocalizedContent,
    },
    decoration: {
      url: '/img/decorative/our-story-shapes-1.svg',
      width: 780,
      height: 758,
    },
    decorationOffset: {
      left: -102,
      top: 0,
    },
  }

  const mobilePhoto2 = {
    image: {
      url: ourStoryPhotos[1]?.url,
      width: 259,
      height: 259,
      caption: ourStoryPhotos[1]?.caption as LocalizedContent,
    },
    decoration: {
      url: '/img/decorative/our-story-mobile-shapes-2.svg',
      width: 350,
      height: 320,
    },
    decorationOffset: {
      left: -72,
      top: -40,
    },
  }

  const desktopPhoto2 = {
    image: {
      url: ourStoryPhotos[1]?.url,
      width: 420,
      height: 420,
      marginLeft: 40,
      caption: ourStoryPhotos[1]?.caption as LocalizedContent,
    },
    decoration: {
      url: '/img/decorative/our-story-shapes-2.svg',
      width: 675,
      height: 535,
    },
    decorationOffset: {
      left: -178,
      top: -76,
    },
  }

  return (
    <main
      id="main-content"
      className="relative mt-4 overflow-x-hidden lg:mt-[80px]"
    >
      <div className="relative container">
        <div className="lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="font-poppins relative z-10">
            <h1 className="text-4 text-purple-7 lg:text-6 font-bold">
              {getLocalizedContent(
                textContent[OurStoryContent.TITLE]?.content,
                locale,
              )}
            </h1>
            <p className="text-3 text-mauve-11 lg:text-4 mt-2 font-light lg:mt-4">
              {getLocalizedContent(
                textContent[OurStoryContent.ABSTRACT]?.content,
                locale,
              )}
            </p>
            <div className="relative lg:hidden">
              <div className="absolute top-[-18px] right-[-72px] w-[235px]">
                <img src="/img/decorative/our-story-shape-1.svg" alt="" />
              </div>
              <div className="absolute top-[63px] left-[-24px] w-[55px]">
                <img src="/img/decorative/our-story-shape-2.svg" alt="" />
              </div>
              <img
                className="rounded-5 relative mt-5"
                src={ourStoryPhotos[0]?.url}
                alt=""
              />
              {ourStoryPhotos[0]?.caption && (
                <p className="text-1 mt-3">
                  {getLocalizedContent(ourStoryPhotos[0].caption, locale)}
                </p>
              )}
            </div>
            <div className="relative mt-5 lg:mt-6">
              <div className="bg-our-story absolute top-[-32px] right-[-135px] h-[329px] w-[329px] lg:hidden" />
              <div className="relative">
                <Markdown
                  options={markdownOptions}
                  text={getLocalizedContent(
                    textContent[OurStoryContent.TEXT]?.content,
                    locale,
                  )}
                />
              </div>
            </div>
          </div>
          <div className="relative z-1 hidden pr-0 pl-8 lg:block">
            <DecoratedImage {...desktopPhoto1} />
          </div>
        </div>
        <div className="flex flex-col lg:mt-9 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="relative flex justify-center pt-[54px] lg:hidden">
            <DecoratedImage {...mobilePhoto2} />
          </div>
          <div className="relative hidden pt-[95px] lg:block">
            <DecoratedImage {...desktopPhoto2} />
          </div>
          <div className="font-poppins relative z-10 mt-[48px] lg:mt-0">
            <div className="absolute top-[-8px] right-0 w-[58px] lg:hidden">
              <img src="/img/decorative/our-story-star.svg" alt="" />
            </div>
            <h2 className="text-4 text-purple-7 lg:text-5 font-bold">
              {getLocalizedContent(
                textContent[OurStoryContent.LEADERSHIP_TITLE]?.content,
                locale,
              )}
            </h2>
            <div className="mt-4 lg:mt-6">
              <Markdown
                options={markdownOptions}
                text={getLocalizedContent(
                  textContent[OurStoryContent.LEADERSHIP_TEXT]?.content,
                  locale,
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="relative container mt-5 flex flex-row justify-center lg:mt-9">
        <div className="bg-our-story-carousel absolute top-[-300px] left-[-161px] h-[408px] w-[408px] lg:top-[-270px] lg:left-[-290px] lg:h-[906px] lg:w-[906px]" />
        <div className="absolute top-[-13px] left-[-8px] w-[78px] lg:top-[258px] lg:left-[12px] lg:w-[150px]">
          <img src="/img/decorative/our-story-dots.svg" alt="" />
        </div>
        <div className="absolute top-[73px] right-[89px] hidden w-[58px] lg:block lg:w-[81px]">
          <img src="/img/decorative/our-story-star.svg" alt="" />
        </div>
        <div>
          <Swiper
            className="w-[320px] lg:w-[742px]"
            modules={[Navigation, Pagination, A11y]}
            navigation={{
              nextEl: '.swiper-navigation__next',
              prevEl: '.swiper-navigation__prev',
            }}
            pagination={{
              el: '.swiper-pagination__custom',
              type: 'bullets',
            }}
            loop
            aria-label={t('ourStorySlideshow')}
          >
            {ourStoryCarouselItems?.map((item, i) => (
              <SwiperSlide
                key={`our-story-carousel-${i}`}
                className="flex flex-col items-center justify-center"
              >
                <div>
                  <div
                    style={{
                      backgroundImage: `url('${item.url}')`,
                      backgroundSize: 'cover',
                    }}
                    className="rounded-6 h-[183px] min-h-[183px] w-[320px] min-w-[320px] lg:h-[415px] lg:min-h-[415px] lg:w-[742px] lg:min-w-[742px]"
                  />
                  {item.caption && (
                    <p className="font-open-sans text-1 relative z-10 mt-3 max-w-[320px] lg:mt-4 lg:max-w-[742px]">
                      {getLocalizedContent(item.caption, locale)}
                    </p>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div>
            <button
              className="swiper-navigation swiper-navigation__prev text-cyan-11! border-cyan-11! hover:bg-gray-200! focus:bg-gray-200! focus:outline-hidden"
              aria-label={t('previous')}
            >
              <Icon className="rotate-180" name="chevron-right" />
            </button>
            <button
              className="swiper-navigation swiper-navigation__next text-cyan-11! border-cyan-11! hover:bg-gray-200! focus:bg-gray-200! focus:outline-hidden"
              aria-label={t('next')}
            >
              <Icon name="chevron-right" />
            </button>
          </div>
          <div className="swiper-pagination__custom" />
        </div>
      </div>

      <div className="mt-9 w-full lg:container lg:mt-0">
        <ContactForm
          headingTextContent={
            textContent[OurStoryContent.CONTACT_FORM_HEADING]?.content
          }
          descriptionTextContent={
            textContent[OurStoryContent.CONTACT_FORM_DESCRIPTION]?.content
          }
          submitButtonTextContent={
            textContent[OurStoryContent.CONTACT_FORM_BUTTON]?.content
          }
          confirmationTextContent={contactFormConfirmation}
          showSubmittedConfirmation={actionData?.contactFormSubmitted}
        />
      </div>
    </main>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        500: ({ error }) => (
          <div className="text-center">
            <h1>{error.data.title}</h1>
            <p className="text-3 mt-2 font-normal">{error.data.description}</p>
          </div>
        ),
      }}
    />
  )
}
